/**
 * The one toast primitive. A small, self-contained notice
 * that slides in under the masthead corner and dismisses itself, so a failure
 * that used to be swallowed silently (a rejected clipboard write, a blocked
 * download) now says so. Ink on paper, a tone strip, a square pip: it reads
 * like the rest of the sheet, not a floating chrome card.
 *
 * Usage: wrap the app once in <ToastProvider>, then call useToast().toast(text).
 * The exit slide is a CSS transition, so it is silenced by the global
 * prefers-reduced-motion block automatically.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  registerStorageToastEmitter,
  type StorageErrorKind,
} from "../utils/storageError";
import { useT, type Locale } from "./i18n";

export type ToastTone = "info" | "ok" | "warn" | "bad";

/** Optional one-shot action rendered as a small button inside the toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  text: string;
  tone: ToastTone;
  leaving: boolean;
  action?: ToastAction;
}

interface ToastApi {
  /** Show a transient notice. Pass an already-localized string. */
  toast: (text: string, tone?: ToastTone, action?: ToastAction) => void;
}

const DISMISS: Record<Locale, string> = {
  en: "Dismiss",
  de: "Schließen",
};

/**
 * Localized copy for classified storage failures. The write sites forward
 * through emitStorageToast (debounced per kind); this provider is the
 * The storage bridge,
 * so a quota hit is no longer silent.
 */
const STORAGE_COPY: Record<Locale, Record<StorageErrorKind, string>> = {
  en: {
    "quota-exceeded":
      "This browser's storage is full; the last preference could not be saved.",
    "blocked-by-other-tab": "Another tab is holding storage; the value was not saved.",
    "access-denied": "Storage access is blocked here; preferences will not persist.",
    corrupted: "A stored value was unreadable and has been reset.",
    unknown: "Saving to this browser's storage failed.",
  },
  de: {
    "quota-exceeded":
      "Der Speicher dieses Browsers ist voll; die letzte Einstellung wurde nicht gespeichert.",
    "blocked-by-other-tab":
      "Ein anderer Tab blockiert den Speicher; der Wert wurde nicht gespeichert.",
    "access-denied":
      "Der Speicherzugriff ist hier blockiert; Einstellungen bleiben nicht erhalten.",
    corrupted: "Ein gespeicherter Wert war unlesbar und wurde zurückgesetzt.",
    unknown: "Das Speichern im Browser ist fehlgeschlagen.",
  },
};

const ToastCtx = createContext<ToastApi | null>(null);

/** Access the toast API. A no-op fallback keeps leaf components safe if they
 *  render outside a provider (tests, isolated stories). */
export function useToast(): ToastApi {
  return useContext(ToastCtx) ?? NOOP;
}
const NOOP: ToastApi = { toast: () => {} };

const AUTO_MS = 4200;
const EXIT_MS = 200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { locale } = useT();
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  /** False after unmount; see `toast` below. */
  const mounted = useRef(true);
  // Track timers so we clear them on unmount and never fire into a gone tree.
  const timers = useRef<number[]>([]);

  // Auto-dismiss timers by toast id so hover/focus can pause them: a reader
  // (or a keyboard user tabbing to the close button) gets to finish reading.
  const autoTimers = useRef<Map<number, number>>(new Map());

  const drop = useCallback((id: number) => {
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
    timers.current.push(
      window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), EXIT_MS),
    );
  }, []);

  const scheduleDrop = useCallback(
    (id: number, ms: number) => {
      const h = window.setTimeout(() => {
        autoTimers.current.delete(id);
        drop(id);
      }, ms);
      autoTimers.current.set(id, h);
    },
    [drop],
  );

  const pause = useCallback((id: number) => {
    const h = autoTimers.current.get(id);
    if (h !== undefined) {
      window.clearTimeout(h);
      autoTimers.current.delete(id);
    }
  }, []);

  /** Restart the full window on leave: simpler than tracking remaining time,
   *  and it errs toward giving the reader longer, never shorter. */
  const resume = useCallback(
    (id: number) => {
      if (!autoTimers.current.has(id)) scheduleDrop(id, AUTO_MS);
    },
    [scheduleDrop],
  );

  const toast = useCallback(
    (text: string, tone: ToastTone = "info", action?: ToastAction) => {
      // An async producer can outlive this provider: a clipboard promise that
      // settles after a route change, a storage write from a torn-down view.
      // Without the guard it queued a timer nothing would ever clear.
      if (!mounted.current) return;
      const id = (idRef.current += 1);
      setItems((xs) => [...xs, { id, text, tone, leaving: false, action }]);
      scheduleDrop(id, AUTO_MS);
    },
    [scheduleDrop],
  );

  useEffect(() => {
    mounted.current = true;
    // The ref objects are stable for the provider's lifetime; read them into
    // locals so the cleanup closes over the same arrays the effect saw.
    const exitTimers = timers.current;
    const dismissTimers = autoTimers.current;
    const alive = mounted;
    return () => {
      alive.current = false;
      exitTimers.forEach((t) => window.clearTimeout(t));
      dismissTimers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  // Bridge the module-level storage-failure channel into this provider so
  // quota / access errors from any write site surface as localized toasts.
  useEffect(() => {
    registerStorageToastEmitter((kind) => {
      const copy = STORAGE_COPY[locale] ?? STORAGE_COPY.en;
      toast(copy[kind] ?? copy.unknown, "warn");
    });
    return () => registerStorageToastEmitter(null);
  }, [locale, toast]);

  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className="nx-toasts" role="status" aria-live="polite">
        {items.map((it) => (
          <div
            key={it.id}
            className={`nx-toast nx-toast--${it.tone}${it.leaving ? " nx-toast--leaving" : ""}`}
            onMouseEnter={() => pause(it.id)}
            onMouseLeave={() => resume(it.id)}
            onFocus={() => pause(it.id)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) resume(it.id);
            }}
          >
            <span className="nx-toast__pip" aria-hidden="true" />
            <span className="nx-toast__text">{it.text}</span>
            {it.action ? (
              <button
                type="button"
                className="nx-btn nx-toast__action"
                onClick={() => {
                  it.action?.onClick();
                  drop(it.id);
                }}
              >
                {it.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="nx-toast__close"
              aria-label={DISMISS[locale] ?? DISMISS.en}
              onClick={() => drop(it.id)}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
